import { DayObject, TimeOfDay } from "@/types/date"
import { BusinessHour } from "@/types/place"

import {
  addDays,
  afterDayObject,
  getTimeOfDay,
  getTimestamp,
  now,
  sameDay,
  today,
  weekday,
} from "@/lib/date-utils"
import { buildTimeList } from "@/lib/time-list"
import { findNearest } from "@/lib/utils/geo"

const DAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
]

// A parsed time range or an opaque backend string ("Closed", "Open 24 hours").
// Kept structured (not pre-formatted) so the component can localize the hour
// cycle via Locale.useFormatTimeOfDayAsHourMinuteRange.
export type BusinessHourEntry =
  | { kind: "range"; start: TimeOfDay; end: TimeOfDay }
  | { kind: "text"; text: string }

function convertStringToTimeOfDay(timeString: string): TimeOfDay {
  const [hour, minute] = timeString.split(":").map(Number)
  return { hour, minute }
}

export function groupHoursByDay(
  data: BusinessHour[]
): Record<string, string[]> {
  const grouped: Record<string, string[]> = {
    monday: ["Closed"],
    tuesday: ["Closed"],
    wednesday: ["Closed"],
    thursday: ["Closed"],
    friday: ["Closed"],
    saturday: ["Closed"],
    sunday: ["Closed"],
  }

  for (const item of data) {
    const dayKey = item.day.toLowerCase()
    grouped[dayKey] = item.times
  }

  return grouped
}

function isStandardTimeRange(timeRange: string): boolean {
  const timeRangeRegex = /^\d{1,2}:\d{2} - \d{1,2}:\d{2}$/
  return timeRangeRegex.test(timeRange)
}

// Parses "HH:MM - HH:MM" ranges (sorted by start) and passes anything else
// through as text. Formatting/localization happens in the component.
export function sortAndFormatHours(hours: string[]): BusinessHourEntry[] {
  const standardRanges: { startMinutes: number; entry: BusinessHourEntry }[] =
    []
  const nonStandardEntries: BusinessHourEntry[] = []

  hours.forEach((timeRange) => {
    if (isStandardTimeRange(timeRange)) {
      const [start, end] = timeRange.split(" - ").map(convertStringToTimeOfDay)
      standardRanges.push({
        startMinutes: start.hour * 60 + start.minute,
        entry: { kind: "range", start, end },
      })
    } else {
      nonStandardEntries.push({ kind: "text", text: timeRange })
    }
  })

  standardRanges.sort((a, b) => a.startMinutes - b.startMinutes)
  return [...standardRanges.map((r) => r.entry), ...nonStandardEntries]
}

export function areAllDaysSame(hours: BusinessHour[]) {
  if (hours.length !== 7) return false

  const allTimes = hours.map((hour) => hour.times.join(", "))
  return new Set(allTimes).size === 1
}

export const OPEN_STATUS = { status: "Open", statusClass: "text-green-10" }
export const CLOSED_STATUS = {
  status: "Closed",
  statusClass: "text-red-10",
}
export const OPENING_SOON_STATUS = {
  status: "Opens soon",
  statusClass: "text-amber-11",
}
export const CLOSING_SOON_STATUS = {
  status: "Closes soon",
  statusClass: "text-amber-11",
}

export function getPlaceStatus(
  hours: BusinessHour[],
  timeZone: string
): { status: string; statusClass: string } {
  const currentDayObject = today(timeZone)
  const currentWeekday = weekday(currentDayObject)

  const todayHours = hours.find(
    (hour) => hour.day === DAY_NAMES[currentWeekday - 1]
  )
  const currentTime = getTimestamp(
    currentDayObject,
    getTimeOfDay(now(), timeZone),
    timeZone
  ).epoch_millis

  if (!todayHours || todayHours.times[0].toLowerCase() === "closed") {
    return CLOSED_STATUS
  }

  if (
    todayHours.times[0].toLowerCase() === "24 hours" ||
    todayHours.times[0].toLowerCase() === "open 24 hours"
  ) {
    return OPEN_STATUS
  }

  for (const timeRange of todayHours.times) {
    const [startTimeString, endTimeString] = timeRange.split(" - ")
    const [startHour, startMinute] = startTimeString.split(":").map(Number)
    const [endHour, endMinute] = endTimeString
      ? endTimeString.split(":").map(Number)
      : [0, 0]

    const startTimestamp = getTimestamp(
      currentDayObject,
      { hour: startHour, minute: startMinute },
      timeZone
    ).epoch_millis
    // if end hour is before start hour, biz closes the next day (e.g. 8pm - 2am)
    const endDay =
      endHour < startHour ? addDays(currentDayObject, 1) : currentDayObject
    const endTimestamp = getTimestamp(
      endDay,
      { hour: endHour, minute: endMinute },
      timeZone
    ).epoch_millis

    if (
      currentTime < startTimestamp &&
      startTimestamp - currentTime <= 1800000
    ) {
      return OPENING_SOON_STATUS
    }

    if (currentTime >= startTimestamp && currentTime <= endTimestamp) {
      return endTimestamp - currentTime <= 3600000
        ? CLOSING_SOON_STATUS
        : OPEN_STATUS
    }
  }

  return CLOSED_STATUS
}

export type BusinessHoursTransition =
  | { kind: "closes"; time: TimeOfDay }
  | { kind: "opens"; time: TimeOfDay; dayOffset: number }

function isOpen24Hours(dayHours: string[] | undefined): boolean {
  const firstEntry = dayHours?.[0]?.toLowerCase()
  return firstEntry === "24 hours" || firstEntry === "open 24 hours"
}

function parseRangeTimestamps(
  timeRange: string,
  day: DayObject,
  timeZone: string
):
  | { start: TimeOfDay; end: TimeOfDay; startMs: number; endMs: number }
  | undefined {
  if (!isStandardTimeRange(timeRange)) {
    return undefined
  }

  const [startTimeString, endTimeString] = timeRange.split(" - ")
  const start = convertStringToTimeOfDay(startTimeString)
  const end = convertStringToTimeOfDay(endTimeString)
  const startMs = getTimestamp(day, start, timeZone).epoch_millis
  const endDay = end.hour < start.hour ? addDays(day, 1) : day
  const endMs = getTimestamp(endDay, end, timeZone).epoch_millis

  return { start, end, startMs, endMs }
}

function findNextOpening(
  hours: BusinessHour[],
  timeZone: string,
  startDay: DayObject,
  currentTimeMs: number
): BusinessHoursTransition | undefined {
  for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
    const day = dayOffset === 0 ? startDay : addDays(startDay, dayOffset)
    const dayName = DAY_NAMES[weekday(day) - 1]
    const dayHours = hours.find(
      (hour) => hour.day.toLowerCase() === dayName.toLowerCase()
    )?.times

    if (!dayHours || isClosedDayHours(dayHours) || isOpen24Hours(dayHours)) {
      continue
    }

    for (const timeRange of dayHours) {
      const parsed = parseRangeTimestamps(timeRange, day, timeZone)
      if (!parsed) {
        continue
      }

      if (dayOffset === 0 && parsed.startMs <= currentTimeMs) {
        continue
      }

      return { kind: "opens", time: parsed.start, dayOffset }
    }
  }

  return undefined
}

export function getNextBusinessHoursTransition(
  hours: BusinessHour[],
  timeZone: string
): BusinessHoursTransition | undefined {
  const currentDayObject = today(timeZone)
  const currentWeekday = weekday(currentDayObject)
  const currentTime = getTimestamp(
    currentDayObject,
    getTimeOfDay(now(), timeZone),
    timeZone
  ).epoch_millis

  const todayHours = hours.find(
    (hour) => hour.day === DAY_NAMES[currentWeekday - 1]
  )?.times

  if (isOpen24Hours(todayHours)) {
    return undefined
  }

  if (!todayHours || isClosedDayHours(todayHours)) {
    return findNextOpening(hours, timeZone, currentDayObject, currentTime)
  }

  for (const timeRange of todayHours) {
    const parsed = parseRangeTimestamps(timeRange, currentDayObject, timeZone)
    if (!parsed) {
      continue
    }

    if (currentTime >= parsed.startMs && currentTime <= parsed.endMs) {
      return { kind: "closes", time: parsed.end }
    }
  }

  return findNextOpening(hours, timeZone, currentDayObject, currentTime)
}

export const DEFAULT_RESERVATION_TIME: TimeOfDay = { hour: 19, minute: 0 }
const RESERVATION_LEAD_MINUTES = 30
const OPEN_24_HOURS_LABELS = new Set(["24 hours", "open 24 hours"])

function timeOfDayToMinutes(time: TimeOfDay): number {
  return time.hour * 60 + time.minute
}

export function timeOfDayToKey(time: TimeOfDay): string {
  return `${String(time.hour).padStart(2, "0")}:${String(time.minute).padStart(2, "0")}`
}

function isSameTimeOfDay(a: TimeOfDay, b: TimeOfDay): boolean {
  return a.hour === b.hour && a.minute === b.minute
}

function isTimeInRange(
  time: TimeOfDay,
  start: TimeOfDay,
  end: TimeOfDay
): boolean {
  const minutes = timeOfDayToMinutes(time)
  const startMinutes = timeOfDayToMinutes(start)
  const endMinutes = timeOfDayToMinutes(end)

  if (endMinutes >= startMinutes) {
    return minutes >= startMinutes && minutes < endMinutes
  }

  return minutes >= startMinutes || minutes < endMinutes
}

function getHoursForDay(
  hours: BusinessHour[],
  date: DayObject
): string[] | undefined {
  const dayName = DAY_NAMES[weekday(date) - 1]
  return hours.find((hour) => hour.day.toLowerCase() === dayName.toLowerCase())
    ?.times
}

function isClosedDayHours(dayHours: string[] | undefined): boolean {
  return dayHours?.[0]?.toLowerCase() === "closed"
}

export function getClosedWeekdays(hours: BusinessHour[]): number[] {
  const closed: number[] = []

  for (const item of hours) {
    if (!isClosedDayHours(item.times)) continue

    const dayIndex = DAY_NAMES.findIndex(
      (name) => name.toLowerCase() === item.day.toLowerCase()
    )
    if (dayIndex === -1) continue

    closed.push((dayIndex + 1) % 7)
  }

  return closed
}

export function isClosedOnDate(
  hours: BusinessHour[],
  date: DayObject
): boolean {
  return isClosedDayHours(getHoursForDay(hours, date))
}

function filterSlotsByBusinessHours(
  slots: TimeOfDay[],
  dayHours: string[] | undefined
): TimeOfDay[] | undefined {
  if (!dayHours || dayHours.length === 0) {
    return slots
  }

  const firstEntry = dayHours[0]?.toLowerCase()
  if (firstEntry === "closed") {
    return []
  }

  if (dayHours.some((entry) => OPEN_24_HOURS_LABELS.has(entry.toLowerCase()))) {
    return slots
  }

  const parsedHours = sortAndFormatHours(dayHours)
  const ranges = parsedHours.filter(
    (entry): entry is Extract<BusinessHourEntry, { kind: "range" }> =>
      entry.kind === "range"
  )

  if (ranges.length === 0) {
    return slots
  }

  return slots.filter((slot) =>
    ranges.some((range) => isTimeInRange(slot, range.start, range.end))
  )
}

function filterPastReservationTimes(
  slots: TimeOfDay[],
  date: DayObject,
  timeZone: string
): TimeOfDay[] {
  if (!sameDay(date, today(timeZone))) {
    return slots
  }

  const minMinutes =
    timeOfDayToMinutes(getTimeOfDay(now(), timeZone)) + RESERVATION_LEAD_MINUTES

  return slots.filter((slot) => timeOfDayToMinutes(slot) >= minMinutes)
}

export function getReservationTimeOptions({
  hours,
  timeZone,
  date,
}: {
  hours?: BusinessHour[]
  timeZone: string
  date?: DayObject
}): TimeOfDay[] {
  if (!date) {
    return []
  }

  const allSlots = buildTimeList(2).map((entry) => entry.value)
  const dayHours = hours ? getHoursForDay(hours, date) : undefined
  const openSlots = filterSlotsByBusinessHours(allSlots, dayHours)

  if (!openSlots) {
    return []
  }

  return filterPastReservationTimes(openSlots, date, timeZone)
}

const DEFAULT_MAX_DAYS_AHEAD = 14

export function getNextAvailableReservationDate({
  hours,
  timeZone,
  fromDate,
  untilDate,
  maxDaysAhead = DEFAULT_MAX_DAYS_AHEAD,
}: {
  hours?: BusinessHour[]
  timeZone: string
  fromDate: DayObject
  /** Prefer a day on or before this date; fall back beyond it only if needed. */
  untilDate?: DayObject
  maxDaysAhead?: number
}): DayObject {
  if (!hours || hours.length === 0) {
    return fromDate
  }

  const findAvailable = (hardUntil?: DayObject): DayObject | undefined => {
    for (let offset = 0; offset <= maxDaysAhead; offset++) {
      const candidate = offset === 0 ? fromDate : addDays(fromDate, offset)
      if (hardUntil && afterDayObject(candidate, hardUntil)) {
        break
      }
      const options = getReservationTimeOptions({
        hours,
        timeZone,
        date: candidate,
      })
      if (options.length > 0) {
        return candidate
      }
    }
    return undefined
  }

  if (untilDate) {
    const withinBound = findAvailable(untilDate)
    if (withinBound) {
      return withinBound
    }
  }

  return findAvailable() ?? fromDate
}

export function getDefaultReservationTime(
  options: TimeOfDay[]
): TimeOfDay | undefined {
  if (options.length === 0) {
    return undefined
  }

  const exactMatch = options.find((option) =>
    isSameTimeOfDay(option, DEFAULT_RESERVATION_TIME)
  )
  if (exactMatch) {
    return exactMatch
  }

  const nearestIndex = findNearest(options, (option) =>
    Math.abs(
      timeOfDayToMinutes(option) - timeOfDayToMinutes(DEFAULT_RESERVATION_TIME)
    )
  )

  return options[nearestIndex]
}

export function getEffectiveReservationTime(
  stored: TimeOfDay | undefined,
  options: TimeOfDay[]
): TimeOfDay | undefined {
  if (options.length === 0) {
    return undefined
  }

  if (stored && options.some((option) => isSameTimeOfDay(option, stored))) {
    return stored
  }

  return getDefaultReservationTime(options)
}
